import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { AppProvider, useApp } from "./context/AppContext";
import { initAnalytics, trackPageView, track } from "./analytics";
import { Seo } from "./components/Seo";
import { waLink } from "./components/LeadCta";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { FloatingButtons } from "./components/FloatingButtons";
import { MobileNav } from "./components/MobileNav";
import { Toaster } from "./components/ui/sonner";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Verification from "./pages/Verification";
import Calculators from "./pages/Calculators";
import NewOrder from "./pages/NewOrder";
import OrderDetail from "./pages/OrderDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import { ForgotPassword, ResetPassword } from "./pages/AuthPages";
import Dashboard from "./pages/Dashboard";
const Admin = lazy(() => import("./pages/Admin"));
import Blog, { BlogPost } from "./pages/Blog";
import Contact from "./pages/Contact";
import RentAgreement from "./pages/RentAgreement";
import DistrictPage from "./pages/DistrictPage";
import { StaticPage, FaqPage } from "./pages/StaticPages";
import { Registration, EStamping, Garvi } from "./pages/InfoPages";

function NotFound() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="not-found-page">
      <Seo title="Page not found" description="The page you're looking for doesn't exist." noindex />
      <p className="font-display font-extrabold text-6xl text-primary/20">404</p>
      <h1 className="font-display font-bold text-2xl mt-2">Page not found / પેજ મળ્યું નથી</h1>
      <p className="text-muted-foreground mt-2 text-sm">The page you're looking for doesn't exist or has moved. Try one of these:</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2" data-testid="not-found-links">
        {[["/", "Home"], ["/order/new/index-2", "Index 2 Copy Online"], ["/order/new/certified-copy", "Certified Copy"], ["/services", "All Property Documents"], ["/garvi", "Garvi 2.0 Guide"], ["/e-stamping", "E-Stamping"], ["/rent-agreement", "Rent Agreement"], ["/blog", "Blog"], ["/contact", "Contact"]].map(([to, label]) => (
          <Link key={to} to={to} className="px-4 py-2 rounded-full border border-border bg-white text-sm font-semibold text-primary hover:border-primary" data-testid={`not-found-link-${to.replace(/\//g, "-") || "home"}`}>{label}</Link>
        ))}
      </div>
      <NotFoundWhatsApp />
    </div>
  );
}

function NotFoundWhatsApp() {
  const { site } = useApp();
  if (!site.whatsapp) return null;
  return (
    <a href={waLink(site.whatsapp, "Hi DocGalaxy, I need help finding a property document.")} target="_blank" rel="noopener noreferrer"
      onClick={() => track("whatsapp_click", { placement: "404" })}
      className="inline-flex items-center gap-2 mt-8 h-12 px-6 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="not-found-whatsapp-btn">
      <MessageCircle size={20} /> WhatsApp પર Document Details મોકલો
    </a>
  );
}

const DISTRICT_SLUGS = ["rajkot", "ahmedabad", "surat", "vadodara", "gandhinagar", "jamnagar", "bhavnagar", "junagadh", "mehsana", "anand", "bharuch", "navsari", "morbi", "gondal"];

function Layout({ children }) {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageView(loc.pathname); }, [loc.pathname]);
  return (
    <div className="min-h-screen flex flex-col pb-14 md:pb-0">
      <Header />
      <main className="flex-1">{children}</main>
      {!isAdmin && <Footer />}
      <FloatingButtons />
      <MobileNav />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/verification" element={<Verification />} />
            <Route path="/calculators" element={<Calculators />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/rent-agreement" element={<RentAgreement />} />
            <Route path="/e-stamping" element={<EStamping />} />
            <Route path="/garvi" element={<Garvi />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/about" element={<StaticPage pageKey="about" />} />
            <Route path="/privacy" element={<StaticPage pageKey="privacy" />} />
            <Route path="/terms" element={<StaticPage pageKey="terms" />} />
            <Route path="/refund-policy" element={<StaticPage pageKey="refund-policy" />} />
            <Route path="/disclaimer" element={<StaticPage pageKey="disclaimer" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin" element={<Suspense fallback={<div className="max-w-7xl mx-auto px-4 py-16 text-center text-muted-foreground">Loading admin...</div>}><Admin /></Suspense>} />
            <Route path="/order/new/:slug" element={<NewOrder />} />
            <Route path="/order/:orderId" element={<OrderDetail />} />
            <Route path="/district/:slug" element={<DistrictPage />} />
            <Route path="/gujarat-property-documents" element={<DistrictPage key="rajkot" slugOverride="rajkot" />} />
            {DISTRICT_SLUGS.map((d) => (
              <Route key={d} path={`/${d}-property-documents`} element={<DistrictPage key={d} slugOverride={d} />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
